import React, { useMemo } from 'react';

/**
 * Generates a moon phase identicon based on a string input
 * Displays one of 30 progressive phases of the moon using outlines and shading
 * The phase is deterministically generated from the input seed
 */
const Identicon = ({ seed, size = 64, marginRight = 12 }) => {
  const moonPhase = useMemo(() => {
    // Create a hash from the input string
    const input = seed.toString();
    let hash = 0;
    
    for (let i = 0; i < input.length; i++) {
      hash = ((hash << 5) - hash) + input.charCodeAt(i);
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    // Get a moon phase (0-29) from the hash
    const phase = Math.abs(hash % 30);
    
    // Calculate the illumination ratio (0 to 1)
    const illumination = phase / 29;
    
    // Determine if we're in waxing (first half) or waning (second half) phase
    const isWaxing = phase <= 14;
    
    return {
      phase,
      illumination,
      isWaxing
    };
  }, [seed]);

  // Generate the SVG path for the moon phase
  const moonSVG = (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Base circle with dark fill */}
      <circle
        cx="50"
        cy="50"
        r="40"
        fill="#888888"
        stroke="#888888"
        strokeWidth="2"
      />
      
      {/* Illuminated portion */}
      {moonPhase.phase !== 0 && moonPhase.phase !== 29 && ( // Skip for new moon
        <path
          d={`
            M 50 10
            A 40 40 0 1 ${moonPhase.isWaxing ? '1' : '0'} 50 90
            A ${40 * (1 - Math.abs(0.5 - moonPhase.illumination) * 2)} 40 0 1 ${moonPhase.isWaxing ? '1' : '0'} 50 10
            Z
          `}
          fill="black"
          stroke="#888888"
          strokeWidth="2"
        />
      )}
    </svg>
  );
  
  // Calculate adequate padding to prevent cutting
  const padding = Math.ceil(size * 0.05);
  
  // If marginRight is 0, return with minimal wrapper
  if (marginRight === 0) {
    return (
      <div style={{ 
        paddingRight: '8px', 
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        minWidth: size + padding,
        minHeight: size + padding
      }}>
        {moonSVG}
      </div>
    );
  }
  
  return (
    <div style={{ 
      marginRight, 
      flexShrink: 0,
      paddingRight: '8px',
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      minWidth: size + padding,
      minHeight: size + padding
    }}>
      {moonSVG}
    </div>
  );
};

export default Identicon; 